import { useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { PageButtonAction, useToast } from '@kcd/design-system';
import { trackEvent } from '@kcd/event-tracker';

import { getCheckoutBeforeRecommendRoute } from '~/constants/checkout/utils';
import { ADDRESS_ROUTER } from '~/constants/router';
import { useDefaultShippingAddress } from '~/domains/address/queries';
import {
  useShoppingCartPrice,
  useValidateShoppingCart,
} from '~/domains/cart/queries';
import { CartOrderMembershipNudge } from '~/domains/plusMembership/components/Nudges';
import { useMembershipRegisteredExpectedAmountNudge } from '~/domains/plusMembership/queries';
import { useOAIInvalidateQueries } from '~/hooks/reactQuery';
import { SHOPPING_CART } from '~/hooks/useCartTypeParam';
import { usePosOpenedByReferrer } from '~/hooks/useReferrer';
import { getOAIErrorMessage } from '~/libs/openapi';
import { OAIApiError } from '~/libs/openApiError';
import { changedByFontScale } from '~/utils/fontScale';
import { appAlert, openPageModal } from '~/utils/mobileApp';

import type { RefProps } from './PosQRModal';
import PosQRModal from './PosQRModal';

import LoadingSpinnerIcon from '~/assets/icons/scb_loading_spinner.svg';

type Props = {
  isCartLoading: boolean;
  isSomeChecked: boolean;
};

const OrderButton = ({ isCartLoading, isSomeChecked }: Props) => {
  const invalidateQueries = useOAIInvalidateQueries();
  const toast = useToast();
  const router = useRouter();
  const address = useDefaultShippingAddress();
  const isPosOpened = usePosOpenedByReferrer();
  const ref = useRef<RefProps | null>(null);
  const [submitLoading, setSubmitLoading] = useState(false);

  const { data: totalPaymentData, isFetching } =
    useShoppingCartPrice('SHOPPING_CART');
  const { data: expectedAmountData } =
    useMembershipRegisteredExpectedAmountNudge();

  const { mutateAsync: validateShoppingCart } = useValidateShoppingCart();

  // 주문하기
  const onConfirm = async () => {
    if (!address) {
      openPageModal(ADDRESS_ROUTER.new(SHOPPING_CART));
    }

    try {
      setSubmitLoading(true);
      await validateShoppingCart({
        cartType: 'SHOPPING_CART',
      });
      const moveUrl = await getCheckoutBeforeRecommendRoute(SHOPPING_CART);
      void router.push(moveUrl);
    } catch (error) {
      if (error instanceof OAIApiError) {
        toast(getOAIErrorMessage(error), { offset: 100 });
      } else {
        appAlert();
      }

      await invalidateQueries(
        '/api/market-place/v3/shopping-cart-items',
        '/api/market-place/v1/shopping-cart-items/total-in-shopping-cart-payment',
      );
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleClick = () => {
    trackEvent('clicked button in 마켓/장바구니', {
      type: 'CTA',
      name: '주문하기',
    });

    if (isPosOpened) {
      ref.current?.onOpen();
      return;
    }

    void onConfirm();
  };

  if (!totalPaymentData) return <></>;

  return (
    <>
      <PageButtonAction
        className="[&>div:last-child>div:first-child]:!mb-0"
        helpText={
          <CartOrderMembershipNudge
            isSomeChecked={isSomeChecked}
            discountedTotalAmount={totalPaymentData.discountedTotalAmount || 0}
            membershipRegisteredExpectedAmount={expectedAmountData?.nudgeAmount}
          />
        }
        primaryButtonProps={{
          children: isSomeChecked ? (
            <>
              {isCartLoading || isFetching ? (
                <LoadingSpinnerIcon className="size-24 text-gray-000" />
              ) : (
                <>
                  {totalPaymentData?.discountedTotalAmount?.toLocaleString()}원
                  {changedByFontScale() && <br />} 주문하기
                </>
              )}
            </>
          ) : (
            '상품을 선택해 주세요'
          ),
          onClick: handleClick,
          disabled: !isSomeChecked || submitLoading,
        }}
      />
      <PosQRModal ref={ref} />
    </>
  );
};

export default OrderButton;
